import React, { useMemo } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { ProductCard } from '@shopmy/ui';

import ShopmyImage from '../General/ShopmyImage';
import { useMoments } from './MomentsContext';
import JudgeBadge from './JudgeBadge';
import StatusChip from './StatusChip';
import { formatUsd, momentPublicUrl } from './moment_stream_helpers';

import './MomentShopPane.scss';

// The live moment preview: hero, status row (status + judge chips, featured
// toggle, archive/retry), and the sectioned product grid (the shared
// @shopmy/ui ProductCard — the Noir request-view card) with remove curation.
// `busyProductIds` disables only the cards with a curation round-trip in flight.
const MomentShopPane = () => {
  const {
    moment,
    actionBusy,
    busyProductIds,
    archive: onArchive,
    retry: onRetry,
    setFeatured: onSetFeatured,
    setProductState: onSetProductState
  } = useMoments();
  // Archive/remove have no UI undo — each confirms first.
  const confirmArchive = () =>
    confirmAlert({
      title: 'Archive Moment',
      message: `Archive "${moment.title}"? It leaves the gallery${moment.status === 'published' ? ' and goes offline' : ''}.`,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: 'Archive', onClick: onArchive }
      ]
    });

  const confirmRemove = product =>
    confirmAlert({
      title: 'Remove product',
      message: `Remove "${product.title}"? The agent will never re-add it to this Moment.`,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: 'Remove', onClick: () => onSetProductState(product.id, 'editor_removed') }
      ]
    });
  const sections = useMemo(() => {
    const ordered = [];
    const byLabel = new Map();
    (moment.products || []).forEach(product => {
      const label = product.section || 'The Edit';
      if (!byLabel.has(label)) {
        const section = { label, products: [] };
        byLabel.set(label, section);
        ordered.push(section);
      }
      byLabel.get(label).products.push(product);
    });
    return ordered;
  }, [moment.products]);

  return (
    <div className='msa-shop-pane'>
      <div className='msa-hero'>
        {moment.heroImageUrl ? (
          <ShopmyImage className='msa-hero-photo' source={moment.heroImageUrl} alt={moment.title} />
        ) : (
          <div className='msa-collage'>
            {(moment.products || [])
              .map(product => product.image)
              .filter(Boolean)
              .slice(0, 5)
              .map((src, i) => (
                <ShopmyImage key={i} source={src} alt='' />
              ))}
          </div>
        )}
        <div className='msa-hero-overlay'>
          <h2>{moment.title}</h2>
        </div>
      </div>
      <div className='msa-shop-body'>
        <div className='msa-status-row'>
          <StatusChip status={moment.status} />
          <JudgeBadge verdict={moment.judgeVerdict} score={moment.judgeScore} />
          {moment.status === 'published' && moment.slug && (
            <a className='msa-live-link' href={momentPublicUrl(moment.slug)} target='_blank' rel='noopener noreferrer'>
              /moments/{moment.slug}
            </a>
          )}
          <div className='msa-status-actions'>
            {moment.status !== 'archived' && (
              <label
                className='msa-featured-toggle'
                title={
                  moment.status === 'published' || moment.featured
                    ? 'Featured Moments join the shopper discover page (/shop/discover/moments)'
                    : 'Only live (published) Moments can be featured'
                }
              >
                <input
                  type='checkbox'
                  checked={!!moment.featured}
                  disabled={actionBusy || (!moment.featured && moment.status !== 'published')}
                  onChange={event => onSetFeatured(event.target.checked)}
                />
                Featured
              </label>
            )}
            {moment.status !== 'archived' && (
              <button className='msa-archive-btn' disabled={actionBusy} onClick={confirmArchive} title='Archive'>
                Archive
              </button>
            )}
            {moment.status === 'failed' && (
              <button className='msa-retry-btn' disabled={actionBusy} onClick={onRetry} title='Re-run the last build message'>
                Retry build
              </button>
            )}
          </div>
        </div>
        <p className='msa-shop-description'>{moment.description}</p>
        <div className='msa-shop-meta'>
          {moment.productCount} products
          {moment.buildCostUsd ? ` · build cost ${formatUsd(moment.buildCostUsd)}` : ''}
        </div>
        {sections.map(section => (
          <div key={section.label} className='msa-section'>
            <h3>{section.label}</h3>
            <div className='msa-grid'>
              {section.products.map(product => (
                // ProductCard sanitizes href internally (http/https only).
                <ProductCard
                  key={product.id}
                  image={product.image}
                  brand={product.brand}
                  title={product.title}
                  price={product.price}
                  href={product.url}
                  meta={product.curatorNote}
                  actions={
                    <button
                      title='Remove — the agent cannot re-add this product'
                      disabled={busyProductIds.has(product.id)}
                      onClick={() => confirmRemove(product)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  }
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MomentShopPane;
